"use client";

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { X } from "lucide-react";

type AnimationStyle =
  | "from-bottom"
  | "from-center"
  | "from-top"
  | "from-left"
  | "from-right"
  | "fade"
  | "top-in-bottom-out"
  | "left-in-right-out";

interface HeroVideoProps {
  animationStyle?: AnimationStyle;
  videoSrc: string;
  // Controlled component props
  isOpen: boolean;
  onClose: () => void;
}

const animationVariants = {
  "from-bottom": {
    initial: { y: "100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "100%", opacity: 0 },
  },
  "from-center": {
    initial: { scale: 0.5, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.5, opacity: 0 },
  },
  "from-top": {
    initial: { y: "-100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "-100%", opacity: 0 },
  },
  "from-left": {
    initial: { x: "-100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "-100%", opacity: 0 },
  },
  "from-right": {
    initial: { x: "100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "100%", opacity: 0 },
  },
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  "top-in-bottom-out": {
    initial: { y: "-100%", opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: "100%", opacity: 0 },
  },
  "left-in-right-out": {
    initial: { x: "-100%", opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: "100%", opacity: 0 },
  },
};

export default function HeroVideoDialog({
  animationStyle = "from-center",
  videoSrc,
  isOpen,
  onClose,
}: HeroVideoProps) {
  const [mounted, setMounted] = useState(false);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't render anything if not mounted
  if (!mounted) return null;

  // Don't render anything if not open
  if (!isOpen) return null;

  const selectedAnimation = animationVariants[animationStyle];

  // Render video dialog
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-labelledby="video-title"
      aria-describedby="video-description"
    >
      <div
        className="relative mx-4 aspect-video w-full max-w-4xl md:mx-0"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.button
          className="absolute -top-16 right-0 rounded-full bg-neutral-900/50 p-2 text-xl text-white ring-1 backdrop-blur-md dark:bg-neutral-100/50 dark:text-black z-10"
          onClick={onClose}
          aria-label="Close video dialog"
        >
          <X className="size-5" />
        </motion.button>
        <motion.div
          {...selectedAnimation}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="relative isolate z-[1] size-full overflow-hidden rounded-2xl border-2 border-white"
        >
          <iframe
            src={videoSrc}
            title="PhotoZoot Demo Video"
            className="size-full rounded-2xl"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          ></iframe>
        </motion.div>
      </div>
    </div>
  );
}
